/**
 * @voltxampere/presets — Diode clipper (#7)
 *
 * V1 (DC) → R → out, with two anti-parallel diodes from `out` to
 * gnd. With a positive DC source, the upper diode (anode at out,
 * cathode at gnd) forward-conducts and clamps V(out) at one
 * Shockley drop above ground (~0.7 V). The lower diode stays
 * reverse-biased.
 *
 * Sprint 25 R2 lane 4D-A — hand-authored layout. V1 vertical at
 * left, R1 horizontal across the top rail, two anti-parallel
 * diodes vertical at right shunting `out` to `gnd`. Body centers
 * rounded to 20-px grid; pin world coords derived from
 * position + rotation per the cheat sheet in the lane brief.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const diodeClipper = {
    id: "diode-clipper",
    name: { tr: "Diyot Kırpıcı", en: "Diode Clipper" },
    category: "filter",
    difficulty: 3,
    description: {
        tr: "Anti-paralel diyotlar ile genlik kırpma. V1=5V, R=1kΩ → V(out)≈±0.7V'a sınırlanır.",
        en: "Anti-parallel diodes clip the signal. V1=5V, R=1kΩ → V(out) clamped to ±0.7V.",
    },
    circuit: makeCircuit({
        slot: 7,
        name: "Diode Clipper",
        description: "Anti-parallel diode clipper",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "in" }),
            makeNet({ name: "out" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 5 },
                terminals: { pos: "net_in", neg: "net_gnd" },
                label: "V1",
                position: { x: -200, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_in", p2: "net_out" },
                label: "R1",
                position: { x: -60, y: -80 },
                rotation: 0,
            }),
            makeComponent({
                id: "D1",
                type: "diode",
                params: { saturationCurrent: 1e-14, ideality: 1 },
                terminals: { anode: "net_out", cathode: "net_gnd" },
                label: "D1",
                position: { x: 60, y: -40 },
                rotation: 90,
            }),
            makeComponent({
                id: "D2",
                type: "diode",
                params: { saturationCurrent: 1e-14, ideality: 1 },
                terminals: { anode: "net_gnd", cathode: "net_out" },
                label: "D2",
                position: { x: 60, y: 40 },
                rotation: 270,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -200, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            // in rail: V1.pos (-200, -10) → R1.p1 (-80, -80)
            {
                id: "w_in",
                netId: "net_in",
                path: [
                    { x: -200, y: -10 },
                    { x: -200, y: -80 },
                    { x: -80, y: -80 },
                ],
                style: "orthogonal",
            },
            // out rail leg A: R1.p2 (-40, -80) → D1.A (60, -60)
            {
                id: "w_out_top",
                netId: "net_out",
                path: [
                    { x: -40, y: -80 },
                    { x: 60, y: -80 },
                    { x: 60, y: -60 },
                ],
                style: "orthogonal",
            },
            // out rail leg B: D1.A (60, -60) → D2.K (60, 20)
            {
                id: "w_out_mid",
                netId: "net_out",
                path: [
                    { x: 60, y: -60 },
                    { x: 60, y: 20 },
                ],
                style: "orthogonal",
            },
            // gnd rail leg A: V1.neg (-200, 10) → GND.p (-200, 71)
            {
                id: "w_gnd_left",
                netId: "net_gnd",
                path: [
                    { x: -200, y: 10 },
                    { x: -200, y: 71 },
                ],
                style: "orthogonal",
            },
            // gnd rail leg B: D1.K (60, -20) → D2.A (60, 60) → GND.p (-200, 71)
            {
                id: "w_gnd_right",
                netId: "net_gnd",
                path: [
                    { x: 60, y: -20 },
                    { x: 60, y: 60 },
                ],
                style: "orthogonal",
            },
            {
                id: "w_gnd_bottom",
                netId: "net_gnd",
                path: [
                    { x: 60, y: 60 },
                    { x: 60, y: 110 },
                    { x: -200, y: 110 },
                    { x: -200, y: 71 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        // With V_in = 5 V and Rseries = 1 kΩ, the upper diode conducts
        // ~4.3 mA → Vd ≈ 0.69 V (Shockley with default Is=1e-14).
        {
            node: "out",
            analysis: "dc",
            value: 0.69,
            tolerance: 0.05,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Doğrusal olmayan kırpma: diyot ileri eşik gerilimi sinyali ±Vd'ye sınırlar.",
            en: "Nonlinear clipping: diode forward threshold limits signal to ±Vd.",
        },
        {
            tr: "Anti-paralel topoloji simetrik kırpma sağlar (her iki yönlü).",
            en: "Anti-parallel topology gives symmetric clipping (both polarities).",
        },
    ],
    references: ["Horowitz/Hill, The Art of Electronics, §1.6"],
};
//# sourceMappingURL=diode-clipper.js.map